import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { Box, Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { type ResolvableModalFC } from '../../components/modals/modalsManager';

import { subscriptionApis } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import {
  discountPctForCents,
  discountedPriceCents,
  formatCents,
  formatCentsExact,
  sanitizeDollars,
} from './usage-format';

const OTHER = 'other';
const NUM = 'font-mono tabular-nums';

interface BuyUsageCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (purchased: boolean) => void;
  teamId?: string;
  paymentMethodOverride?: { brand: string; last4: string } | null;
  onEditPaymentMethod?: () => void;
  onRequestUpgrade?: (source: string) => void;
}

export const BuyUsageCreditsModal: ResolvableModalFC<
  BuyUsageCreditsModalProps,
  boolean
> = ({
  open,
  onOpenChange,
  resolve,
  teamId,
  paymentMethodOverride,
  onEditPaymentMethod,
  onRequestUpgrade,
}) => {
  const { setHash } = useHashRoute();
  const paymentMethod = teamId
    ? paymentMethodOverride
    : subscriptionStore.state.paymentMethod;

  const { data, isLoading } = useQuery({
    queryKey: ['credit-packs'],
    queryFn: subscriptionApis.getCreditPacks,
  });
  const packs = data?.bundles ?? [];
  const brackets = data?.discount_brackets ?? [];
  const minCents = data?.min_value_cents ?? 500;
  const maxCents = data?.max_value_cents ?? 200_000;
  const rateCentsPer1000 = data?.rate_cents_per_1000;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [otherDollarsStr, setOtherDollarsStr] = useState('');
  const [busy, setBusy] = useState(false);
  const isBusyRef = useRef(false);

  const effectiveSelectedId = selectedId ?? packs[0]?.id ?? null;
  const isOther = effectiveSelectedId === OTHER;
  const otherDollars =
    otherDollarsStr === '' ? null : parseFloat(otherDollarsStr);
  const otherCents =
    otherDollars != null && Number.isFinite(otherDollars)
      ? Math.round(otherDollars * 100)
      : null;
  const valueCents = isOther
    ? otherCents
    : (packs.find((pack) => pack.id === effectiveSelectedId)?.value_cents ??
      null);

  const discountPct =
    valueCents != null ? discountPctForCents(brackets, valueCents) : 0;
  const priceCents =
    valueCents != null ? discountedPriceCents(valueCents, discountPct) : null;

  const otherBoundError =
    isOther && otherCents != null
      ? otherCents < minCents
        ? `Minimum amount is ${formatCents(minCents)}`
        : otherCents > maxCents
          ? `Maximum amount is ${formatCents(maxCents)}`
          : null
      : null;
  const isValid =
    valueCents != null && valueCents >= minCents && valueCents <= maxCents;

  const close = (purchased: boolean) => {
    resolve(purchased);
    onOpenChange(false);
  };

  const handlePay = async () => {
    if (valueCents == null || !isValid || isBusyRef.current) return;
    isBusyRef.current = true;
    setBusy(true);
    trackEvent('CREDIT_PURCHASE_STARTED', {
      value_cents: valueCents,
      price_cents: priceCents,
      discount_pct: discountPct,
      is_team: !!teamId,
    });
    try {
      const result = await subscriptionApis.purchaseCreditPack(
        { value_cents: valueCents, idempotency_key: crypto.randomUUID() },
        rateCentsPer1000,
        teamId ? { teamId } : undefined
      );
      if (result?.status === 'succeeded') close(true);
      else if (result?.status === 'requires_action') close(false);
    } finally {
      isBusyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close(false))}
    >
      <Dialog.Content className="max-w-lg gap-5">
        <Dialog.Header>
          <Dialog.Title className="font-serif text-2xl">
            Buy usage credits
          </Dialog.Title>
          <Dialog.Description>
            Credits are spent once your monthly usage runs out. Top up any time.
          </Dialog.Description>
        </Dialog.Header>

        {isLoading ? (
          <BuyUsageCreditsSkeleton />
        ) : (
          <YStack className="gap-5">
            <Grid className="grid-cols-2 sm:grid-cols-4 gap-3 items-start">
              {packs.map((pack) => (
                <PackOption
                  key={pack.id}
                  label={formatCents(pack.value_cents)}
                  discount={
                    pack.discount_pct > 0
                      ? `Save ${pack.discount_pct}%`
                      : undefined
                  }
                  selected={effectiveSelectedId === pack.id}
                  disabled={busy}
                  onSelect={() => setSelectedId(pack.id)}
                />
              ))}
              <PackOption
                label="Other"
                note="Custom"
                selected={isOther}
                disabled={busy}
                onSelect={() => setSelectedId(OTHER)}
              />
            </Grid>

            {isOther && (
              <YStack className="gap-1.5">
                <span className="text-sm font-medium text-foreground-subtle">
                  Custom amount
                </span>
                <Box className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-muted pointer-events-none">
                    $
                  </span>
                  <Input
                    value={otherDollarsStr}
                    onChange={(e) =>
                      setOtherDollarsStr(sanitizeDollars(e.target.value))
                    }
                    placeholder="0"
                    disabled={busy}
                    className={cn('pl-7', NUM)}
                  />
                </Box>
                <span className="block min-h-4 text-xs leading-4 text-destructive">
                  {otherBoundError}
                </span>
              </YStack>
            )}

            <YStack className="gap-2.5 px-4 py-3.5 bg-surface border border-border rounded-lg">
              <XStack className="items-center justify-between text-sm">
                <span className="text-foreground-muted">Usage credits</span>
                <span className={cn('text-foreground', NUM)}>
                  {formatCentsExact(
                    isValid && valueCents != null ? valueCents : 0
                  )}
                </span>
              </XStack>
              <XStack className="items-center justify-between text-sm">
                <span className="text-foreground-muted">Discount</span>
                <span className={cn('text-foreground', NUM)}>
                  {isValid &&
                  valueCents != null &&
                  priceCents != null &&
                  discountPct > 0
                    ? `Save ${discountPct}% (−${formatCentsExact(valueCents - priceCents)})`
                    : '—'}
                </span>
              </XStack>
              <div className="h-px bg-border" />
              <XStack className="items-baseline justify-between">
                <span className="text-sm font-medium text-foreground">
                  Total due
                </span>
                <span
                  className={cn('text-xl font-semibold text-foreground', NUM)}
                >
                  {formatCentsExact(
                    isValid && priceCents != null ? priceCents : 0
                  )}
                </span>
              </XStack>
            </YStack>

            {paymentMethod && (
              <XStack className="items-center justify-between px-4 py-3 border border-border rounded-lg">
                <span className="text-sm text-foreground">Payment method</span>
                <XStack className="items-center gap-2">
                  <span className={cn('text-sm text-foreground-muted', NUM)}>
                    {paymentMethod.brand === 'link'
                      ? 'Link'
                      : `${paymentMethod.brand.toUpperCase()} •••• ${paymentMethod.last4}`}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onEditPaymentMethod
                        ? onEditPaymentMethod()
                        : subscriptionApis.createPortalSession()
                    }
                    disabled={busy}
                  >
                    Edit
                  </Button>
                </XStack>
              </XStack>
            )}

            <Button
              className="w-full h-11 text-base"
              onClick={handlePay}
              isLoading={busy}
              disabled={busy || !isValid || priceCents == null}
            >
              {isValid && priceCents != null
                ? `Pay ${formatCentsExact(priceCents)}`
                : 'Pay now'}
            </Button>

            {!teamId && subscriptionStore.canUpgrade && (
              <XStack className="justify-center">
                <span className="text-center text-xs text-foreground-muted">
                  Consistently running out? Plan upgrades include monthly usage
                  at a better rate than one-time credits.{' '}
                  <button
                    type="button"
                    className="font-medium text-accent hover:underline"
                    onClick={() => {
                      trackEvent('UPGRADE_BUTTON_CLICKED', {
                        source: 'buy_credits_modal',
                      });
                      close(false);
                      if (onRequestUpgrade) {
                        onRequestUpgrade('buy_credits_modal');
                      } else {
                        setHash('settings/Subscription');
                      }
                    }}
                  >
                    View plans
                  </button>
                </span>
              </XStack>
            )}
          </YStack>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

const BuyUsageCreditsSkeleton = () => (
  <YStack className="gap-5">
    <Grid className="grid-cols-2 sm:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-lg" />
      ))}
    </Grid>
    <YStack className="gap-2.5 px-4 py-3.5 border border-border rounded-lg">
      <XStack className="items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-16" />
      </XStack>
      <XStack className="items-center justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-12" />
      </XStack>
      <div className="h-px bg-border" />
      <XStack className="items-center justify-between">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-6 w-24" />
      </XStack>
    </YStack>
    <Skeleton className="h-[3.25rem] w-full rounded-lg" />
    <Skeleton className="h-11 w-full rounded-lg" />
  </YStack>
);

const PackOption = ({
  label,
  discount,
  note,
  selected,
  disabled,
  onSelect,
}: {
  label: string;
  discount?: string;
  note?: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    className={cn(
      'flex min-h-20 w-full flex-col items-start justify-center gap-2 rounded-lg border border-border-strong p-3 text-left shadow-sm',
      'disabled:cursor-not-allowed disabled:opacity-50',
      selected
        ? 'border-border-strongest bg-surface-active'
        : 'hover:enabled:border-border-strongest'
    )}
  >
    <span className="text-base font-medium">{label}</span>
    {discount && (
      <span className="inline-flex h-[1.125rem] items-center rounded-full bg-success-3 px-2 text-[0.6875rem] font-medium leading-none text-success-11">
        {discount}
      </span>
    )}
    {note && <span className="text-xs text-foreground-muted">{note}</span>}
  </button>
);
